import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

import { api } from '../../api/client'
import { usePolling } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { CountSeries, StackedCountSeries } from '../charts'
import { Widget, type MisuraWidget } from '../Griglia'
import { severityLabel } from '../patching/labels'
import { ElencoBarre, NumeroGrande, type RigaBarra } from './primitivi'

/**
 * The patching widgets of the customisable dashboard (03/11/2026, «il cruscotto
 * del patching»): coverage, SLA by environment, time to remediate, new against
 * fixed, the patches that take away most risk, the exceptions to decide. Each
 * reads an API the Patching page already uses.
 */

const ogniCinque = 5 * 60_000
const PERIODO = {
  key: 'days', predefinito: '90', label: () => t('Periodo'),
  valori: [{ value: '30', label: () => t('{n} giorni', { n: 30 }) }, { value: '90', label: () => t('{n} giorni', { n: 90 }) },
           { value: '180', label: () => t('{n} giorni', { n: 180 }) }, { value: '365', label: () => t('Un anno') }],
}
const giorni = (o: Record<string, unknown>) => Number(o.days ?? 90)
const apri = (ancora: string) => <Link to={`/patching#${ancora}`}>{t('apri')}</Link>

function Copertura() {
  const s = usePolling(() => api.patchCoverage(), ogniCinque)
  const d = s.data
  return (
    <Widget titolo={t('Copertura del patching')} caricando={s.loading} errore={s.error} link={apri('copertura')}>
      {d && <NumeroGrande valore={d.coverage != null ? `${d.coverage} %` : '—'} tono={d.coverage != null && d.coverage < 90 ? 'warn' : 'ok'}
                          etichetta={t('computer letti dall’interno')}
                          sotto={t('{n} solo dalla rete, {v} con la lettura vecchia', { n: d.by_category.network_only, v: d.by_category.stale })} />}
    </Widget>
  )
}

function SlaAmbienti() {
  const s = usePolling(() => api.patchSummary(), ogniCinque)
  const righe: RigaBarra[] = (s.data?.compliance.by_environment ?? []).map((g) => {
    const quota = g.hosts ? Math.round((100 * g.within_sla) / g.hosts) : 0
    return { key: g.environment, nome: g.environment === '-' ? t('senza ambiente') : g.environment,
             sotto: t('{n} macchine', { n: g.hosts }), valore: quota, testo: `${quota} %`,
             tono: quota >= 95 ? 'ok' : quota >= 80 ? 'warn' : 'error' }
  })
  return (
    <Widget titolo={t('Entro lo SLA, per ambiente')} caricando={s.loading} errore={s.error} link={apri('panoramica')}>
      <ElencoBarre righe={righe} vuoto={t('Nessuna macchina.')} />
    </Widget>
  )
}

function Mttr({ options }: { options: Record<string, unknown> }) {
  const g = giorni(options)
  const s = usePolling(() => api.patchLifecycle(g), ogniCinque, [g])
  const righe: RigaBarra[] = ['critica', 'alta', 'media', 'bassa'].map((sev) => {
    const m = s.data?.mttr[sev]
    return { key: sev, nome: severityLabel(sev), sotto: m?.count ? t('{n} corrette', { n: m.count }) : undefined,
             valore: m?.mean ?? 0, testo: m?.mean != null ? t('{n} giorni', { n: m.mean }) : '—' }
  })
  return (
    <Widget titolo={t('Tempo medio di correzione')} sotto={t('ultimi {n} giorni', { n: g })} caricando={s.loading} errore={s.error} link={apri('ciclo-di-vita')}>
      <ElencoBarre righe={righe} vuoto={t('Nessuna correzione nel periodo.')} />
    </Widget>
  )
}

function NuoveCorrette({ options }: { options: Record<string, unknown> }) {
  const g = giorni(options)
  const s = usePolling(() => api.patchLifecycle(g), ogniCinque, [g])
  return (
    <Widget titolo={t('Patch nuove e corrette')} sotto={t('ultimi {n} giorni', { n: g })} caricando={s.loading} errore={s.error} link={apri('ciclo-di-vita')}>
      {s.data && (
        <StackedCountSeries altezza={150}
          points={s.data.weeks.map((w) => ({ date: w.week, values: { detected: w.detected, fixed: w.fixed } }))}
          series={[{ key: 'detected', label: t('nuove'), color: 'var(--serie-1)' }, { key: 'fixed', label: t('corrette'), color: 'var(--ok)' }]} />
      )}
    </Widget>
  )
}

function AndamentoRischio() {
  const s = usePolling(() => api.patchSummary(), ogniCinque)
  // The fleet's total risk day by day (patch_compliance_snapshot, 0243); the days before it are 0.
  const punti = (s.data?.trend ?? []).filter((p) => p.risk).map((p) => ({ date: p.day, value: p.risk }))
  return (
    <Widget titolo={t('Andamento del rischio')} sotto={t('il rischio di ogni patch mancante su ogni macchina, sommato')} caricando={s.loading} errore={s.error} link={apri('report')}>
      {s.data && (punti.length < 2
        ? <p className="text-muted">{t('L’andamento si costruisce da oggi: un punto al giorno.')}</p>
        : <CountSeries points={punti} tone="error" label={t('rischio totale')} altezza={150} />)}
    </Widget>
  )
}

function PiuRischio() {
  const s = usePolling(() => api.patchCatalog(), ogniCinque)
  // The risk taken away by installing a patch: its score on every machine missing it.
  const righe: RigaBarra[] = (s.data?.patches ?? [])
    .map((p) => ({ p, peso: p.priority * p.host_count }))
    .sort((a, b) => b.peso - a.peso).slice(0, 10)
    .map(({ p, peso }) => ({ key: p.key, nome: <span className="mono">{p.name}</span>,
                             sotto: t('rischio {r} × {n} macchine', { r: p.priority, n: p.host_count }),
                             valore: peso, testo: String(peso),
                             tono: p.priority_label === 'urgent' ? 'error' : p.priority_label === 'high' ? 'warn' : undefined }))
  return (
    <Widget titolo={t('Le patch che tolgono più rischio')} caricando={s.loading} errore={s.error} link={apri('patch')}>
      <ElencoBarre righe={righe} vuoto={t('Nessuna patch mancante.')} />
    </Widget>
  )
}

function Eccezioni() {
  const s = usePolling(() => api.patchExceptions(), ogniCinque)
  const tutte = s.data?.exceptions ?? []
  const daDecidere = tutte.filter((e) => e.status === 'requested').length
  const inScadenza = tutte.filter((e) => e.in_force && e.days_left <= 14).length
  return (
    <Widget titolo={t('Eccezioni sulle patch')} caricando={s.loading} errore={s.error} link={apri('eccezioni')}>
      {s.data && <NumeroGrande valore={daDecidere} tono={daDecidere ? 'warn' : 'ok'} etichetta={t('da decidere')}
                               sotto={t('{n} attive, {s} scadono entro 14 giorni', { n: tutte.filter((e) => e.in_force).length, s: inScadenza })} />}
    </Widget>
  )
}

export interface DefinizionePatching {
  tipo: string
  size: MisuraWidget
  tall?: boolean
  label: () => string
  descrizione: () => string
  opzioni?: typeof PERIODO[]
  Componente: (p: { options: Record<string, unknown>; sinottico: boolean }) => ReactNode
}

export const CATALOGO_PATCHING: DefinizionePatching[] = [
  { tipo: 'patch-copertura', size: 's', label: () => t('Copertura del patching'), descrizione: () => t('Quanti computer CENSO legge dall’interno, e quanti solo dalla rete.'), Componente: () => <Copertura /> },
  { tipo: 'patch-sla', size: 'm', label: () => t('Entro lo SLA, per ambiente'), descrizione: () => t('La quota di macchine con le patch entro i tempi, ambiente per ambiente.'), Componente: () => <SlaAmbienti /> },
  { tipo: 'patch-mttr', size: 'm', label: () => t('Tempo medio di correzione'), descrizione: () => t('In quanti giorni si correggono le patch, per gravità.'), opzioni: [PERIODO], Componente: (p) => <Mttr {...p} /> },
  { tipo: 'patch-andamento', size: 'l', label: () => t('Patch nuove e corrette'), descrizione: () => t('Settimana per settimana, quante patch mancanti nascono e quante si chiudono.'), opzioni: [PERIODO], Componente: (p) => <NuoveCorrette {...p} /> },
  { tipo: 'patch-andamento-rischio', size: 'l', label: () => t('Andamento del rischio'), descrizione: () => t('Il rischio totale del parco giorno per giorno: scende quando le patch che contano vengono installate.'), Componente: () => <AndamentoRischio /> },
  { tipo: 'patch-rischio', size: 'm', tall: true, label: () => t('Le patch che tolgono più rischio'), descrizione: () => t('Le dieci patch che, installate, tolgono più rischio dal parco.'), Componente: () => <PiuRischio /> },
  { tipo: 'patch-eccezioni', size: 's', label: () => t('Eccezioni sulle patch'), descrizione: () => t('Le richieste da decidere e quelle in scadenza.'), Componente: () => <Eccezioni /> },
]
